const { scrollMotion } = await import(globalThis.MeriAssets?.scrollMotion || './scroll-motion.js');

const headers = new Map();
function register(root = document, excluded) {
  for (const header of root.querySelectorAll('[data-header-scroll]')) {
    if (excluded?.contains(header)) continue;
    if (headers.has(header)) continue;
    const host = header.closest('.shopify-section');
    if (!host) continue;
    host.classList.add('header-motion-host');
    let previous = window.scrollY;
    let distance = 0;
    let direction = 0;
    let height = header.offsetHeight;
    // Keyboard focus and anchor jumps must not land under the sticky header (WCAG 2.4.11).
    const reserve = () => {
      host.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.scrollPaddingTop = `${height}px`;
    };
    reserve();
    const show = () => host.classList.remove('header-motion-hidden');
    // Editor unload fires before the outgoing section leaves the DOM.
    const firstSection = [...(document.querySelector('#MainContent')?.children || [])].find(
      (section) => !excluded?.contains(section),
    );
    const hero = firstSection?.matches('.shopify-section')
      ? firstSection.querySelector(':scope > .motion-banner:not(.motion-banner--video)')
      : null;
    let heroVisibility;
    const observeHero = () => {
      heroVisibility?.disconnect();
      if (!hero) return;
      heroVisibility = new IntersectionObserver(
        ([entry]) => host.classList.toggle('header-over-hero', entry.isIntersecting),
        { rootMargin: `-${height}px 0px 0px 0px` },
      );
      heroVisibility.observe(hero);
    };
    observeHero();
    const size = new ResizeObserver(([entry]) => {
      const nextHeight = entry.borderBoxSize?.[0]?.blockSize || entry.contentRect.height;
      if (nextHeight === height) return;
      height = nextHeight;
      reserve();
      observeHero();
    });
    size.observe(header);
    const events = new AbortController();
    header.addEventListener('focusin', show, { signal: events.signal });
    const unsubscribe = scrollMotion.subscribe((scroll, reduced) => {
      const delta = scroll - previous;
      previous = scroll;
      const next = Math.sign(delta);
      if (next && next !== direction) {
        distance = 0;
        direction = next;
      }
      distance += delta;
      // Only a pending hide needs the DOM checks; ordinary scroll frames skip them.
      const held = () =>
        header.contains(document.activeElement) ||
        header.querySelector('.nav-dropdown[open], .site-menu[open]:not([data-desktop="true"])') ||
        document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])');
      if (header.dataset.headerScroll !== 'true' || scroll < height + 32 || reduced) {
        show();
        distance = 0;
      } else if (distance < -12) {
        show();
        distance = 0;
      } else if (distance > 36) {
        if (held()) show();
        else host.classList.add('header-motion-hidden');
        distance = 0;
      }
      host.classList.toggle('header-motion-scrolled', scroll > height);
    });
    headers.set(header, () => {
      unsubscribe();
      size.disconnect();
      heroVisibility?.disconnect();
      events.abort();
      host.classList.remove('header-motion-host', 'header-motion-hidden', 'header-motion-scrolled', 'header-over-hero');
      host.style.removeProperty('--header-height');
      document.documentElement.style.removeProperty('scroll-padding-top');
    });
  }
}
register();
// Rebuilding resets the header to visible and re-measures it, so editing an
// unrelated section must not do it. Only the header itself or the first main
// section (which decides the hero overlap) matters; reordering can change both.
function affectsHeader(event) {
  const section = event.target;
  if (event.type === 'shopify:section:reorder' || !(section instanceof Element)) return true;
  return (
    Boolean(section.querySelector('[data-header-scroll]')) ||
    section === document.querySelector('#MainContent')?.firstElementChild
  );
}
for (const eventName of ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:unload']) {
  document.addEventListener(eventName, (event) => {
    if (!affectsHeader(event)) return;
    for (const dispose of headers.values()) dispose();
    headers.clear();
    register(document, eventName.endsWith('unload') ? event.target : null);
  });
}
